import { Typography } from '@mui/material'
import { Formik } from 'formik'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { TOS } from 'src/components/shared/TOS'
import { SubmitOpportunityApplication } from 'src/constants/actionTypes'
import { vehicleFormTypes } from 'src/constants/vehicle'
import { PublicOpportunity } from 'src/interfaces/opportunity'
import { createAsyncAction } from 'src/utils/reduxUtils'
import parsePhoneNumber from 'libphonenumber-js'
import { OpportunityApplicationFormValues } from './OpportunityApplicationForm.interface'
import { buildOpportunityApplicationSchema } from './OpportunityApplicationForm.validator'

const PHONE_MASK = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

interface Props {
  publicToken: string
  opportunity: PublicOpportunity
  onMarketChange(marketId: string): void
  onSuccess(): void
}

export const OpportunityApplicationForm = ({
  publicToken,
  opportunity,
  onMarketChange,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const isMultiMarket = opportunity.markets.length > 1

  const marketOptions = useMemo(() => {
    return opportunity.markets.map((market) => ({ label: market.title, value: market.id }))
  }, [opportunity.markets])

  // Labels come from the app's single vehicle-type source rather than a second map, so the wording here cannot
  // drift from the rest of onboarding. The API decides which types this opportunity offers.
  const vehicleOptions = useMemo(() => {
    const labelByValue = new Map(
      vehicleFormTypes.map(({ label, value }) => {
        return [value as string, label]
      }),
    )

    return opportunity.vehicleTypes.map((vehicleType) => ({
      label: labelByValue.get(vehicleType) || vehicleType,
      value: vehicleType,
    }))
  }, [opportunity.vehicleTypes])

  const initValues = useMemo<OpportunityApplicationFormValues>(
    () => ({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      vehicleType: '',
      // With a single market there is nothing to choose, so it is pre-filled and never shown as a control.
      partnerMarketId: isMultiMarket ? '' : opportunity.markets[0]?.id || '',
      acceptOptIn: false,
    }),
    [isMultiMarket, opportunity.markets],
  )

  const validationSchema = useMemo(() => {
    return buildOpportunityApplicationSchema(isMultiMarket)
  }, [isMultiMarket])

  const handleFormSubmit = async (values: OpportunityApplicationFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const parsedPhone = parsePhoneNumber(values.phone, 'US')
      const phone = parsedPhone?.formatInternational().replace(/\s+/g, '') || values.phone

      await createAsyncAction(
        dispatch,
        SubmitOpportunityApplication.request({
          publicToken,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone,
          vehicleType: values.vehicleType,
          partnerMarketId: values.partnerMarketId,
        }),
      )

      onSuccess()
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Could not submit your application')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initValues}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, handleChange }) => (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmit()
          }}
        >
          <FormikTextField
            name="firstName"
            css={tw`w-full mb-4`}
            label="First name*"
            placeholder="Enter your first name"
            inputProps={{ 'data-testid': 'opportunity-apply-first-name-input' }}
          />
          <FormikTextField
            name="lastName"
            css={tw`w-full mb-4`}
            label="Last name*"
            placeholder="Enter your last name"
            inputProps={{ 'data-testid': 'opportunity-apply-last-name-input' }}
          />
          <FormikTextField
            name="email"
            css={tw`w-full mb-4`}
            label="Email address*"
            placeholder="Enter your email address"
            inputProps={{ 'data-testid': 'opportunity-apply-email-input' }}
          />
          <FormikTextField
            name="phone"
            css={tw`w-full mb-4`}
            mask={PHONE_MASK}
            label="Phone number*"
            placeholder="Enter your phone number"
            inputProps={{ 'data-testid': 'opportunity-apply-phone-input' }}
          />
          {/* Margin on a wrapper, not on the select. A margin passed to FormikSelect via `css` never reaches the
              DOM — measured 0px, leaving the next field's label flush against this one — even though the same
              pattern does work on FormikTextField above. Keep the wrapper; the market select below does the same. */}
          <div css={tw`mb-4`}>
            <FormikSelect
              name="vehicleType"
              css={tw`w-full`}
              label="What will you drive?*"
              placeholder="Select a vehicle type"
              options={vehicleOptions}
              inputProps={{ 'data-testid': 'opportunity-apply-vehicle-input' }}
            />
          </div>

          {isMultiMarket ? (
            <div css={tw`mb-4`}>
              <FormikSelect
                name="partnerMarketId"
                css={tw`w-full`}
                label="Which market do you want to work in?*"
                placeholder="Select a market"
                options={marketOptions}
                inputProps={{ 'data-testid': 'opportunity-apply-market-input' }}
                onChange={(event) => {
                  handleChange('partnerMarketId')(event)
                  onMarketChange(event.target.value)
                }}
              />
              <Typography variant="body2" css={tw`text-shades-400 mt-1`}>
                {`${opportunity.partnerDisplayName} is hiring for this role in ${opportunity.markets.length} markets.`}
              </Typography>
            </div>
          ) : (
            <div css={tw`mb-4`}>
              {/* Matches BasicTextField's label exactly so it sits in line with the other fields. */}
              <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
                Market
              </Typography>
              {/*
                Height and text size are pinned to the inputs above rather than left to the content: those render at
                47px with 16px text, so anything smaller reads as a squashed field under the vehicle select. The
                border is inside the height here, unlike the outlined inputs where it is a fieldset overlay.
              */}
              <div
                css={tw`box-border h-[47px] flex items-center px-3 rounded-lg bg-shades-50 border border-solid border-shades-100`}
                data-testid="opportunity-apply-market-readonly"
              >
                <Typography variant="body1" css={tw`text-shades-500`}>
                  {opportunity.markets[0]?.title}
                </Typography>
              </div>
              <Typography variant="body2" css={tw`text-shades-400 mt-1`}>
                {`Set by ${opportunity.partnerDisplayName} for this opportunity.`}
              </Typography>
            </div>
          )}

          <div css={tw`mb-4 mt-6`}>
            <FormikCheckbox
              css={tw`w-full`}
              name="acceptOptIn"
              label="By providing your phone number, you agree to receive SMS and RCS messages from GigSafe. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for support.*"
              inputProps={{ 'data-testid': 'opportunity-apply-opt-in-input' }}
            />
          </div>

          <TOS css={tw`mb-6`} />
          <FormErrorMessage css={tw`mb-4`} error={error} />

          <Button
            type="submit"
            className={tw`w-full`}
            loading={isLoading}
            data-testid="opportunity-apply-submit-btn"
          >
            Submit application
          </Button>
          <Typography variant="body2" css={tw`text-shades-400 mt-3 text-center`}>
            Applying does not start onboarding, and you do not need an account.
          </Typography>
        </form>
      )}
    </Formik>
  )
}
